import { Injectable, signal } from '@angular/core';
import { MARKET_WATCHES_LIMIT, MarketWatch, MW_NEW_NAME_DEFAULT_VALUE, WatchUser } from '../models/market-watch';
import { TendersSearchService } from './tenders-search.service';
import { ApiTenderService } from './api/api-tender.service';
import { DbTenderTerritory } from '../models/tender-territory';
import { TenderTopic } from '../models/tender-topic';
import { firstValueFrom, interval, lastValueFrom } from 'rxjs';
import { ExplainModuleEnum } from '../../shared/services/module-manager.service';
import { ApiExStatisticsService } from '../../shared/services/api/ex-statistics/api-ex-statistics.service';
import { isEqual } from 'lodash';
import { TenderEntityService } from './tender-entity.service';
import { ActivatedRoute } from '@angular/router';
import { findPeriod, toDayPeriod } from '../../shared/helpers/date-helper';
import { DropdownItem } from '../../common-explain/components/ex-dropdown/ex-dropdown.component';
import { DbStatusItem, StatusItem } from '../models/tender-status';
import { ExclusionStatus, Tender } from "../models/tender";
import { SortDirEnum } from "../../common-explain/models/sort";
import { SortFieldEnum } from "../models/tender-search-body";
import { DbTendersUser } from '../../models/user';
import { ApiUserService } from '../../shared/services/api/user/api-user.service';

export interface MarketWatchManager {
  marketWatch?: MarketWatch;
  newMarketWatchName?: string;
  service: TendersSearchService;
  exclusionsService?: TendersSearchService;
  editMode: boolean;
  creationMode?: boolean;
}


const PRELOAD_CHECK_INTERVAL = 1000;
const PRELOAD_BATCH_SIZE = 2;

@Injectable()
export class TendersModuleService {

  marketWatchManagers: MarketWatchManager[] = [];
  previousManager!: MarketWatchManager;
  currentManager!: MarketWatchManager; // uniquement pour veilles.
  activeSearchServices: TendersSearchService[] = [];
  boardSearchService!: TendersSearchService; // uniquement pour vue board.

  tenderTopics!: Promise<TenderTopic[]>;
  user!: Promise<DbTendersUser>;
  groupAccountUsers!: Promise<DbTendersUser[]>;
  userRegions: Promise<DbTenderTerritory[]>;
  groupAccountStatuses = signal<DbStatusItem[]>([])
  statusItems: DropdownItem<StatusItem>[] = [];
  defaultStatusItems: DropdownItem<StatusItem>[] = [];
  customStatusItems: DropdownItem<StatusItem>[] = [];

  status_order = [
    "opportunite",
    "a_creuser",
    "pas_interessant",
    "go",
    "no_go",
    "repondu",
    "gagne",
    "perdu",
  ]

  constructor(
    private apiTenderService: ApiTenderService,
    private apiExStatisticsService: ApiExStatisticsService,
    private apiUserService: ApiUserService,
    private tenderEntityService: TenderEntityService,
    private route: ActivatedRoute
  ) {
    const userId = +(localStorage.getItem('user_id') ?? 0);
    this.initTenderTopics();
    this.userRegions = lastValueFrom(this.apiTenderService.territory.getRegions()).then(async regions => {
      const userDepartments = (await firstValueFrom(this.apiExStatisticsService.AccountInfoReplaySubject))
        .territories
        .map(territory => territory.code);
      return regions.reduce((acc, region) => {
        region.territories = region.territories?.filter(territory => userDepartments
          .includes(territory.code));
        if (region.territories?.length) acc.push(region);
        return acc;
      }, [] as DbTenderTerritory[]);
    });
    const fields = ['created_at', 'id', 'owner_user_id', 'users', 'name', 'settings_json', 'topics_ids', 'updated_at', 'day_of_week', 'market_watch_dates'];
    const filters = {user_id: userId};
    this.apiTenderService.marketWatch.getUserMarketWatches(fields, filters).then(
      marketWatches => {
        if (!marketWatches.length) return;
        this.marketWatchManagers =
          marketWatches.map(watch => {
            const marketWatch = new MarketWatch(watch);
            const service = new TendersSearchService(this.apiTenderService);
            const exclusionsService = new TendersSearchService(this.apiTenderService);
            const period = findPeriod(new Date(), marketWatch.dayOfWeek, marketWatch.marketWatchDates);
            service.watchFilters = {
              ...marketWatch.watchFilters,
              exclusion_status: [ExclusionStatus.INCLUDED, ExclusionStatus.ERROR, ExclusionStatus.PENDING],
              indexation_period: period,
              market_watch_id: marketWatch.id,
              propagate: false
            };
            exclusionsService.watchFilters = {
              ...marketWatch.watchFilters,
              exclusion_status: [ExclusionStatus.EXCLUDED],
              indexation_period: period,
              market_watch_id: marketWatch.id,
              propagate: false
            };
            return {
              marketWatch,
              service,
              exclusionsService,
              editMode: false,
            }
          });
        this.marketWatchManagers.sort((a, b) => new Date(a.marketWatch?.createdAt ?? '').getTime() - new Date(b.marketWatch?.createdAt ?? '').getTime());
        // Ajout initial de tous les services des veilles dans la liste des services actifs.
        this.marketWatchManagers.forEach(manager => this.registerServices(manager));
        const {id, to_date} = this.route.snapshot.queryParams;
        this.setCurrentManager(this.marketWatchManagers.find(manager => manager.marketWatch?.id == +id) ?? this.marketWatchManagers[0]);
        if (to_date) this.setIndexationPeriods(to_date);
      }
    );
    // Preload clock
    interval(PRELOAD_CHECK_INTERVAL).subscribe(() => {
      if (
        this.marketWatchManagers.some(manager => manager.service.inSearch) ||
        this.tenderEntityService.tenderId$.value
      ) return;
      this.marketWatchManagers
        .filter(manager => !manager.service.tenders && !manager.service.inSearch)
        .slice(0, PRELOAD_BATCH_SIZE).forEach(manager => {
        manager.service.search();
        if (manager.exclusionsService) manager.exclusionsService.search();
      })
    });
    this.updateStatusItems()

    this.initGroupAccountUsers();
  }

  addManager(marketWatchManager: MarketWatchManager) {
    this.marketWatchManagers.push(marketWatchManager);
    this.registerServices(marketWatchManager);
  }

  setCurrentManager(marketWatchManager: MarketWatchManager) {
    this.previousManager = this.currentManager;
    this.currentManager = marketWatchManager;
    if (!this.currentManager.service.tenders && !this.currentManager.service.inSearch) this.currentManager.service.search();
    if (this.currentManager.marketWatch?.settingsJson.exclusions?.length &&
      this.currentManager.exclusionsService && !this.currentManager.exclusionsService.tenders && !this.currentManager.exclusionsService.inSearch) this.currentManager.exclusionsService.search();
  }
  
  setIndexationPeriods(toDate: Date) {
    this.currentManager.service.selectedIndexationPeriod = toDayPeriod(toDate);
    if (this.currentManager.exclusionsService) this.currentManager.exclusionsService.selectedIndexationPeriod = toDayPeriod(toDate);
  }

  initGroupAccountUsers() {
    const accountUsers = lastValueFrom(this.apiUserService.getGroupAccountTendersUsers());
    const user = accountUsers.then((users) =>
      users.find((user) => user.id === +(localStorage.getItem('user_id') ?? 0))!);
    if (user) this.user = user
    this.groupAccountUsers = accountUsers.then((users) =>
      users.filter(user => user.id !== +(localStorage.getItem('user_id') ?? 0)));
  }

  async initTenderTopics() {
    this.tenderTopics = lastValueFrom(this.apiTenderService.topic.getUserTopics(ExplainModuleEnum.TENDERS))

    // Update topics for loaded market watches
    if (this.marketWatchManagers.length) {
      const tenderTopicsIds = (await this.tenderTopics).map(topic => topic.id);
      this.marketWatchManagers.forEach(manager => {
        const settings = manager.service?.watchFilters;
        if (!settings) return;
        const userIsOwner = manager?.marketWatch?.ownerUserId === +(localStorage.getItem('user_id') ?? 0);
        // on ne filtre les thèmes que si l'utilisateur est propriétaire de la veille
        const filteredTopics = settings?.topics_ids?.filter(topic => userIsOwner ? tenderTopicsIds.includes(topic) : true) ?? [];
        if (!isEqual(manager.service.selectedTopics, filteredTopics)) {
          manager.service.watchFilters = {
            ...settings, market_watch_id: manager.marketWatch?.id, topics_ids: filteredTopics
          };
          if (manager.marketWatch) manager.marketWatch.topicsIds = filteredTopics;
        }
      });
    }
  }

  async getCurrentMarketWatchUsers(): Promise<WatchUser[]> {
    const copy: WatchUser[] = [];
    const watchUsers = await this.groupAccountUsers;
    watchUsers?.forEach((user) => {
      copy.push({
        ...user,
        is_market_watch_user: (this.currentManager.marketWatch?.watchUsers ?? []).some(elm => elm.id === user.id)
      })
    });
    return copy;
  }

  onDaySelect(date: Date | string) {
    this.currentManager.service.selectedIndexationPeriod = toDayPeriod(date);
    if (this.currentManager.exclusionsService) this.currentManager.exclusionsService.selectedIndexationPeriod = toDayPeriod(date);
  }

  hasNewMarketWatch() {
    return this.marketWatchManagers.some((manager) => !manager.marketWatch)
  }

  newMarketWatch() {
    if (this.marketWatchManagers.length > MARKET_WATCHES_LIMIT || this.hasNewMarketWatch()) return;
    this.addManager({
      service: new TendersSearchService(this.apiTenderService),
      exclusionsService: undefined,
      editMode: true,
      creationMode: true
    });
    this.setCurrentManager(this.marketWatchManagers[this.marketWatchManagers.length - 1]);
    this.currentManager.newMarketWatchName = MW_NEW_NAME_DEFAULT_VALUE;
    this.onDaySelect(new Date());
    this.currentManager.service.sort = { dir: SortDirEnum.DESC, field: SortFieldEnum.RELEVANCE };
  }

  removeCurrentManager() {
    this.removeManager(this.currentManager);
  }

  removeManager(manager: MarketWatchManager) {
    this.marketWatchManagers = this.marketWatchManagers.filter((man) => man !== manager);
    if (this.currentManager === manager) {
      const nextManager = this.marketWatchManagers.includes(this.previousManager) ?
        this.previousManager :
        this.marketWatchManagers[0]
      if (nextManager) this.setCurrentManager(nextManager);
    }
    if (!this.marketWatchManagers.length) this.newMarketWatch();
    this.unregisterServices(manager);
    manager.service.destroy();
  }

  updateTenderInRegisteredServices(tenderId: string, attributes: Partial<Tender>) {
    this.activeSearchServices.forEach((searchService) => {
      searchService.tenders?.forEach((tender) => {
        if (tender.id === tenderId) {
          Object.assign(tender, attributes);
        }
      });
    });
  }

  registerService(service: TendersSearchService | undefined) {
    if (service) this.activeSearchServices.push(service);
  }

  registerServices(manager: MarketWatchManager) {
    const services = [manager.service, manager.exclusionsService];
    services.forEach(service => this.registerService(service));
  }

  unregisterService(service: TendersSearchService | undefined) {
    this.activeSearchServices = this.activeSearchServices.filter(activeService => activeService !== service);
  }

  unregisterServices(manager: MarketWatchManager) {
    const services = [manager.service, manager.exclusionsService];
    services.forEach(service => this.unregisterService(service));
  }

  async updateStatusItems() {
    const statuses = (await this.apiTenderService.groupAccountStatus.retrieveGroupAccountStatuses({
      fields: ['id', 'status_name', 'displayed_status_name', 'color', 'status_type'],
      filters: {},
      limit: 100
    })).data
    const statusItems = statuses.map((elm) => {
      return {
        label: elm.displayed_status_name,
        value: new StatusItem(elm)
      };
    })
    this.defaultStatusItems = statusItems
      .filter((item) => item.value.status_type === 'default')
      .sort((a, b) =>
        this.status_order.findIndex((name) => name === a.value.name) - this.status_order.findIndex((name) => name === b.value.name)
      )
    this.customStatusItems = statusItems.filter((item) => item.value.status_type === 'custom')
      .sort((a, b) => a.label?.localeCompare(b.label))
    this.statusItems = this.defaultStatusItems
      .concat(this.customStatusItems)
    this.groupAccountStatuses.set(statuses);
  }
}

